import { useState } from "react";
import { CRITERIA_DISCIPLINES, criterionKey, effectiveCriterion, resolveFieldOptions } from "../../config/takeoffCriteria";
import { LEGACY_COUNT_CRITERIA, LEGACY_COUNT_CONSUMERS } from "../../config/roomElements";

// Discipline criteria for the Room Takeoff.
//
// Two pieces:
//  - RoomCriteriaExpand — the per-room drill-down rendered inside the room
//    schedule: expand a room (+) → its four discipline sub-rows → expand one
//    → that room's criteria fields and the CS-responsible scope items.
//  - TakeoffCriteriaPanel — the project-level defaults block below the
//    schedule (one set of specs per discipline + effective totals).
//
// A discipline is "active" when CleanSpace holds the R mark on at least one
// non-intake Scope & RACI item in it. Per-room values resolve:
// room override → room-type default → project default (see takeoffCriteria.js).

const label11 = {
  fontSize: 11, fontWeight: 700, color: "var(--text3)",
  textTransform: "uppercase", letterSpacing: "0.05em",
};

const SOURCE_TAG = { template: "from room template", project: "from project default" };

// ── Per-room drill-down (rendered under an expanded room row) ─────────────────
export function RoomCriteriaExpand({ room, criteria, scopeByDiscipline, scopeExclusions, isLocked, onSave, onToggleScope }) {
  const [openDisc, setOpenDisc] = useState({});   // discKey -> bool

  return (
    <div style={{ padding: "4px 10px 10px 34px", background: "var(--surface2)", borderBottom: "1px solid var(--cs-border)" }}>
      {CRITERIA_DISCIPLINES.filter(d => !d.projectWide).map(disc => {
        const allItems = scopeByDiscipline[disc.scopeDiscipline] || [];
        // Room-centric scope only in the room drill-down; project-centric
        // items live in the Discipline Criteria panel below the schedule.
        const csItems = allItems.filter(it => it.ScopeLevel === "room");
        const active = allItems.length > 0;
        const appliedCount = csItems.filter(it => !scopeExclusions?.has(`${room.RoomID}|${it.ScopeItemID}`)).length;
        const open = !!openDisc[disc.key];
        // Real design criteria (engine inputs) vs. the legacy per-room COUNT
        // fields Room Build-Up supersedes (roomElements.js LEGACY_COUNT_CRITERIA).
        // The counts stay editable — their consumers are mapped, not migrated
        // (docs/implementation/P0_LEGACY_COUNT_CONSUMERS.md) — but sit in a
        // demoted block so they aren't mistaken for criteria, the same split
        // the Room Template editor already makes.
        const allPerRoom = disc.fields.filter(f => f.perRoom);
        const perRoomFields = allPerRoom.filter(f => !LEGACY_COUNT_CRITERIA.has(f.key));
        const legacyFields = allPerRoom.filter(f => LEGACY_COUNT_CRITERIA.has(f.key));
        const projectFields = disc.fields.filter(f => !f.perRoom);
        const renderCountField = (f, legacy = false) => {
          const own = criteria[criterionKey(disc.key, f.key, room.RoomID)] ?? "";
          const eff = effectiveCriterion(criteria, disc.key, f.key, room);
          const info = legacy ? LEGACY_COUNT_CONSUMERS[f.key] : null;
          const inherit = eff.source && eff.source !== "override" ? `${eff.value} ${SOURCE_TAG[eff.source]} — type to override, clear to inherit` : "";
          const title = info ? [inherit, `Still read by: ${info.readBy.join("; ")}.`, `Replacement: ${info.replacement}.`].filter(Boolean).join("\n") : inherit;
          return (
            <div key={f.key} style={{ width: 130 }}>
              <div style={{ ...label11, marginBottom: 3 }}>{f.label}</div>
              <input
                type="number"
                min={0}
                defaultValue={own}
                disabled={isLocked}
                placeholder={eff.source && eff.source !== "override" ? eff.value : "—"}
                title={title}
                onBlur={e => { const v = e.target.value; if (v !== own) onSave(disc.key, f.key, room.RoomID, v); }}
                style={{ width: "100%", fontSize: 12, textAlign: "right", border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 6px", background: "var(--surface)", color: own ? "var(--text)" : "var(--text3)" }}
              />
            </div>
          );
        };

        return (
          <div key={disc.key} style={{ borderLeft: `2px solid ${active ? "var(--cs-blue)" : "var(--cs-border)"}`, marginTop: 6 }}>
            <button
              onClick={() => setOpenDisc(p => ({ ...p, [disc.key]: !p[disc.key] }))}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "5px 10px", background: "none", border: "none", cursor: "pointer", opacity: active ? 1 : 0.55 }}
            >
              <span style={{ fontSize: 10, color: "var(--text3)", width: 10 }}>{open ? "▾" : "▸"}</span>
              <span style={{ fontSize: 13 }}>{disc.icon}</span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text)" }}>{disc.label}</span>
              {active ? (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 100, background: "var(--tint-green)", color: "#166534" }}>
                  {csItems.length > 0 ? `${appliedCount}/${csItems.length} in this room` : "CS scope"}
                </span>
              ) : (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 100, background: "var(--tint-amber)", color: "#92400e" }}>
                  outside CS scope
                </span>
              )}
              {/* Effective summary when collapsed: the counts that will price */}
              {!open && allPerRoom.length > 0 && (
                <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: 6 }}>
                  {allPerRoom.map(f => {
                    const eff = effectiveCriterion(criteria, disc.key, f.key, room);
                    return eff.value !== "" ? `${eff.value} ${f.label.toLowerCase()}` : null;
                  }).filter(Boolean).join(" · ")}
                </span>
              )}
            </button>

            {open && (
              <div style={{ padding: "2px 10px 10px 28px" }}>
                {/* This room's per-room engine inputs */}
                {perRoomFields.length > 0 && (
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 8 }}>
                    {perRoomFields.map(f => renderCountField(f))}
                  </div>
                )}

                {/* Legacy per-room quantity fields — demoted, still editable */}
                {legacyFields.length > 0 && (
                  <div style={{ marginBottom: 8, padding: "6px 8px", border: "1px dashed var(--cs-border)", borderRadius: 6 }}>
                    <div style={{ fontSize: 10.5, color: "var(--text3)", marginBottom: 6 }}>
                      <strong>Legacy quantity fields</strong> — not design criteria. Room Build-Up elements are the scope model; these counts remain because
                      circuits, room formulas and the derived scope list still read them (hover a field for its readers). Engine overrides here are the same
                      value the HEPA / lighting element card edits.
                    </div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                      {legacyFields.map(f => renderCountField(f, true))}
                    </div>
                  </div>
                )}

                {/* Project-level specs shown read-only for context */}
                {projectFields.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {projectFields.map(f => {
                      const v = criteria[criterionKey(disc.key, f.key, null)];
                      return (
                        <span key={f.key} title="Project-level spec — set in Discipline Criteria below the schedule" style={{ fontSize: 11, padding: "2px 8px", borderRadius: 100, background: "#eef2f7", color: v ? "var(--text2)" : "var(--text3)" }}>
                          {f.label}: <strong>{v || "not set"}</strong>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Room-centric CS scope: checked = applies to this room */}
                {csItems.length > 0 && (
                  <div style={{ border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)" }}>
                    {csItems.map((it, i) => {
                      const excluded = scopeExclusions?.has(`${room.RoomID}|${it.ScopeItemID}`);
                      return (
                        <label key={it.ScopeItemID} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "4px 10px", borderBottom: i < csItems.length - 1 ? "1px solid var(--cs-border)" : "none", fontSize: 11.5, cursor: isLocked ? "default" : "pointer", opacity: excluded ? 0.5 : 1 }}>
                          <input
                            type="checkbox"
                            checked={!excluded}
                            disabled={isLocked}
                            onChange={e => onToggleScope(room, it.ScopeItemID, !e.target.checked)}
                            style={{ position: "relative", top: 1, flexShrink: 0 }}
                          />
                          <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", flexShrink: 0, width: 84 }}>{it.Phase}</span>
                          <span style={{ color: "var(--text2)", textDecoration: excluded ? "line-through" : "none" }}>{it.Activity}</span>
                          {it.SpecAnswer && <span style={{ color: "var(--text3)", fontStyle: "italic" }}>— {it.SpecAnswer}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
                {csItems.length === 0 && active && (
                  <div style={{ fontSize: 11, color: "var(--text3)" }}>
                    No room-level scope items in this discipline — its CS scope is project-level (see Discipline Criteria below the schedule).
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Project-level defaults block (below the schedule) ─────────────────────────
export default function TakeoffCriteriaPanel({ rooms, criteria, scopeByDiscipline, isLocked, onSave, bodOptions }) {
  const [openInactive, setOpenInactive] = useState({});
  const [openScope, setOpenScope]       = useState({});   // discKey -> bool

  const val = (disc, key) => criteria[criterionKey(disc, key, null)] ?? "";

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 2 }}>Discipline Criteria — project defaults</div>
      <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 10 }}>
        One set of specs per discipline; rooms inherit their counts from the room type first, then
        these defaults. Expand a room (+) in the schedule above for the per-room view.
      </div>

      {CRITERIA_DISCIPLINES.map(disc => {
        const csItems = scopeByDiscipline[disc.scopeDiscipline] || [];
        const projectItems = csItems.filter(it => it.ScopeLevel !== "room");
        const active = csItems.length > 0;
        const perRoomFields = disc.fields.filter(f => f.perRoom);

        if (!active && !openInactive[disc.key]) return (
          <div key={disc.key} style={{ border: "1px dashed var(--cs-border)", borderRadius: 8, padding: "10px 14px", marginBottom: 8, opacity: 0.65, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 15 }}>{disc.icon}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text2)" }}>{disc.label}</span>
            <span style={{ fontSize: 11, color: "var(--text3)" }}>
              Not in CleanSpace scope per the Scope &amp; RACI register
            </span>
            <button
              onClick={() => setOpenInactive(p => ({ ...p, [disc.key]: true }))}
              style={{ marginLeft: "auto", fontSize: 11, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer" }}
            >
              open anyway
            </button>
          </div>
        );

        return (
          <div key={disc.key} style={{ border: "1px solid var(--cs-border)", borderRadius: 8, marginBottom: 10, background: "var(--surface)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--cs-border)", background: "var(--surface2)" }}>
              <span style={{ fontSize: 15 }}>{disc.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{disc.label}</span>
              {active ? (
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 100, background: "var(--tint-green)", color: "#166534" }}>
                  CS scope · {csItems.length} item{csItems.length === 1 ? "" : "s"}
                </span>
              ) : (
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 100, background: "var(--tint-amber)", color: "#92400e" }}>
                  outside CS scope
                </span>
              )}
              {/* Effective totals across all rooms — what line generation will consume */}
              {perRoomFields.length > 0 && rooms.length > 0 && (
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text2)", fontVariantNumeric: "tabular-nums" }}>
                  {perRoomFields.map(f => {
                    const total = rooms.reduce((s, room) => s + (Number(effectiveCriterion(criteria, disc.key, f.key, room).value) || 0), 0);
                    return `${total.toLocaleString()} ${f.label.toLowerCase()}`;
                  }).join(" · ")}
                </span>
              )}
              {projectItems.length > 0 && (
                <button
                  onClick={() => setOpenScope(p => ({ ...p, [disc.key]: !p[disc.key] }))}
                  style={{ marginLeft: perRoomFields.length > 0 && rooms.length > 0 ? 10 : "auto", fontSize: 11, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  {openScope[disc.key] ? "hide project scope ▴" : `project scope (${projectItems.length}) ▾`}
                </button>
              )}
            </div>

            {/* Project-centric CS scope items — apply once, not per room */}
            {openScope[disc.key] && projectItems.length > 0 && (
              <div style={{ borderBottom: "1px solid var(--cs-border)" }}>
                {projectItems.map((it, i) => (
                  <div key={it.ScopeItemID} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "4px 14px", borderBottom: i < projectItems.length - 1 ? "1px solid var(--cs-border)" : "none", fontSize: 11.5 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", flexShrink: 0, width: 90 }}>{it.Phase}</span>
                    <span style={{ color: "var(--text2)" }}>{it.Activity}</span>
                    {it.SpecAnswer && <span style={{ color: "var(--text3)", fontStyle: "italic" }}>— {it.SpecAnswer}</span>}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "10px 16px", padding: "12px 14px" }}>
              {disc.fields.map(f => (
                <div key={f.key}>
                  <div style={{ ...label11, marginBottom: 3 }}>
                    {f.label}{f.perRoom && <span title="Room-type defaults win over this; override per room in the schedule" style={{ color: "var(--cs-blue)", marginLeft: 4, textTransform: "none" }}>per-room</span>}
                  </div>
                  {f.type === "select" ? (
                    <select
                      value={val(disc.key, f.key)}
                      disabled={isLocked}
                      onChange={e => onSave(disc.key, f.key, null, e.target.value)}
                      style={{ width: "100%", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "5px 6px", background: "var(--surface)", color: val(disc.key, f.key) ? "var(--text)" : "var(--text3)" }}
                    >
                      <option value="">— not set —</option>
                      {resolveFieldOptions(bodOptions, disc.key, f).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      type="number"
                      min={0}
                      defaultValue={val(disc.key, f.key)}
                      disabled={isLocked}
                      placeholder="fallback when type has none"
                      onBlur={e => { const v = e.target.value; if (v !== val(disc.key, f.key)) onSave(disc.key, f.key, null, v); }}
                      style={{ width: "100%", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "5px 6px", background: "var(--surface)", color: "var(--text)" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
